import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { generatePDF } from '../../utils/pdfUtils';
import { generateRomanPDF } from '../../utils/pdfUtils';
import { NOTES, transposeChord, ALL_KEYS, getRomanNumeral } from '../../utils/chordUtils';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';

// Helper for deep clone
function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

const SongLibrary = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSong, setSelectedSong] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSong, setPreviewSong] = useState(null);
  const [transposedKey, setTransposedKey] = useState('');
  const [originalKey, setOriginalKey] = useState('');
  const [showRoman, setShowRoman] = useState(false);
  const theme = useTheme();

  const fetchSongs = async () => {
    try {
      const songsRef = collection(db, 'songs');
      const q = query(songsRef, where('userId', '==', auth.currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      const songList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSongs(songList);
    } catch (error) {
      setError('Error fetching songs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSongs();

    const handleRefresh = () => {
      fetchSongs();
    };

    window.addEventListener('refreshSongLibrary', handleRefresh);

    return () => {
      window.removeEventListener('refreshSongLibrary', handleRefresh);
    };
  }, []);

  const handleEdit = (song) => {
    // We'll implement this in App.jsx to switch tabs and load the song
    window.dispatchEvent(new CustomEvent('editSong', { detail: song }));
  };

  const handleDelete = async (songId) => {
    if (window.confirm('Are you sure you want to delete this song?')) {
      try {
        await deleteDoc(doc(db, 'songs', songId));
        await fetchSongs(); // Refresh the list
      } catch (error) {
        setError('Error deleting song: ' + error.message);
      }
    }
  };

  const handleExportPDF = async (song) => {
    try {
      const pdfBytes = await generatePDF(song);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${song.title || 'song'}.pdf`;
      link.click();
    } catch (error) {
      setError('Error generating PDF: ' + error.message);
    }
  };

  const handlePreview = (song) => {
    setSelectedSong(song);
    setPreviewSong(deepClone(song));
    setTransposedKey(song.key);
    setOriginalKey(song.key);
    setPreviewOpen(true);
  };

  // Music-theory-accurate transposition for preview/export
  const transposeSongToKey = (song, fromKey, toKey) => {
    if (fromKey === toKey) return { ...song, key: toKey };
    return {
      ...song,
      key: toKey,
      sections: song.sections.map(section => ({
        ...section,
        content: section.content.map(line => ({
          ...line,
          chords: line.chords
            ? line.chords.split(/(\s+)/).map(part => part.trim() ? transposeChord(part, fromKey, toKey) : part).join('')
            : ''
        }))
      }))
    };
  };

  const handleKeyChange = (newKey) => {
    setPreviewSong(transposeSongToKey(selectedSong, originalKey, newKey));
    setTransposedKey(newKey);
  };

  const handleTranspose = (steps) => {
    const currentKeyIndex = NOTES.indexOf(transposedKey);
    const newKeyIndex = (currentKeyIndex + steps + 12) % 12;
    const newKey = NOTES[newKeyIndex];
    setPreviewSong(transposeSongToKey(selectedSong, originalKey, newKey));
    setTransposedKey(newKey);
  };

  const handleExportTransposed = async () => {
    try {
      const pdfBytes = await generatePDF(previewSong);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${previewSong.title}_${previewSong.key}.pdf`;
      link.click();
    } catch (error) {
      setError('Error generating PDF: ' + error.message);
    }
  };

  const handleExportRomanNumerals = async () => {
    try {
      const pdfBytes = await generateRomanPDF(previewSong);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${previewSong.title}_${previewSong.key}_roman.pdf`;
      link.click();
    } catch (error) {
      setError('Error generating Roman numeral PDF: ' + error.message);
    }
  };

  if (loading) return <Typography>Loading songs...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        My Songs
      </Typography>

      <TableContainer 
        component={Paper} 
        sx={{ 
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            px: { xs: 1, sm: 2 },
            whiteSpace: 'nowrap',
          }
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>Sections</TableCell>
              <TableCell align="right">
                <Box sx={{ minWidth: { xs: 200, sm: 'auto' } }}>
                  Actions
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {songs.map((song) => (
              <TableRow key={song.id}>
                <TableCell>{song.title}</TableCell>
                <TableCell>{song.key}</TableCell>
                <TableCell>{song.sections.length}</TableCell>
                <TableCell align="right">
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.5,
                    justifyContent: 'flex-end',
                    flexWrap: 'nowrap'
                  }}>
                    <Button size="small" onClick={() => handlePreview(song)}>
                      Preview
                    </Button>
                    <Button size="small" onClick={() => handleEdit(song)}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => handleExportPDF(song)}>
                      PDF
                    </Button>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => handleDelete(song.id)}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 },
            width: '100%',
            maxHeight: '90vh',
            bgcolor: theme.palette.background.paper,
            p: { xs: 1, sm: 3 },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1
          }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: theme.palette.text.primary }}>
              {selectedSong?.title}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexWrap: 'wrap'
            }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ color: theme.palette.text.secondary }}>Key</InputLabel>
                <Select
                  value={transposedKey}
                  label="Key"
                  onChange={(e) => handleKeyChange(e.target.value)}
                  sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, color: theme.palette.text.primary }}
                >
                  {Object.keys(ALL_KEYS).map(note => (
                    <MenuItem key={note} value={note}>{note}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button size="small" onClick={() => handleTranspose(-1)} sx={{ minWidth: 32, color: theme.palette.primary.main }}>♭</Button>
              <Button size="small" onClick={() => handleTranspose(1)} sx={{ minWidth: 32, color: theme.palette.primary.main }}>♯</Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: { xs: 1, sm: 2 },
          fontSize: { xs: '0.9rem', sm: '1rem' }
        }}>
          <Slide in direction="down">
            <Box sx={{ mb: 2 }}>
              <Button
                variant={showRoman ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setShowRoman((prev) => !prev)}
                sx={{ mb: 2, fontSize: { xs: '0.95rem', sm: '1rem' }, minWidth: 180, backgroundColor: showRoman ? theme.palette.primary.main : 'transparent', color: showRoman ? theme.palette.primary.contrastText : theme.palette.text.primary, border: showRoman ? 'none' : `1px solid ${theme.palette.primary.main}` }}
              >
                {showRoman ? 'Hide Numbers' : 'Show Numbers'}
              </Button>
            </Box>
          </Slide>
          <Fade in>
            <Box>
              {previewSong?.sections.map((section, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, color: theme.palette.secondary.main, fontWeight: 700 }}
                  >
                    {section.type}
                  </Typography>
                  {section.content.map((line, lineIndex) => (
                    <Box key={lineIndex}>
                      <Typography 
                        sx={{ 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          color: theme.palette.highlight.main,
                          fontWeight: 700,
                          fontSize: { xs: '0.8rem', sm: '0.9rem' },
                          mb: 0.2,
                        }}
                      >
                        {line.chords
                          ? line.chords.split(/(\s+)/).map((part, idx) =>
                              part.trim()
                                ? part
                                : part
                            ).join('')
                          : ''}
                      </Typography>
                      {showRoman && (
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            mb: 0.5,
                          }}
                        >
                          {line.chords
                            ? line.chords.split(/(\s+)/).map((part, idx) =>
                                part.trim()
                                  ? getRomanNumeral(part, previewSong.key) || '-'
                                  : part
                              ).join('')
                            : ''}
                        </Typography>
                      )}
                      <Typography 
                        sx={{ 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          color: theme.palette.text.primary,
                          mb: 1,
                          fontSize: { xs: '0.9rem', sm: '1rem' }
                        }}
                      >
                        {line.lyrics}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1, sm: 2 } }}>
          <Button size="small" onClick={() => setPreviewOpen(false)} sx={{ minWidth: 80, color: theme.palette.text.primary }}>Close</Button>
          <Button size="small" onClick={handleExportTransposed} sx={{ minWidth: 120, backgroundColor: theme.palette.highlight.main, color: theme.palette.highlight.contrastText, '&:hover': { backgroundColor: theme.palette.primary.main, color: '#fff' } }}>
            Export PDF ({transposedKey})
          </Button>
          <Button size="small" color="primary" onClick={handleExportRomanNumerals} sx={{ minWidth: 180, backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { backgroundColor: theme.palette.highlight.main, color: '#fff' } }}>
            Export Numbers Only
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SongLibrary; 